import { Button } from "@progress/kendo-react-buttons";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import {
  Maximize2,
  Minimize2,
  PanelRightClose,
  PanelRightOpen,
  Sparkles,
  X,
} from "lucide-react";
import React, { Suspense, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFrozenValue } from "@/components/slide-panel";
import { useResizableWidth } from "@/components/slide-panel/useResizableWidth";
import { GUARDIAN_MAX_DOCKED_VW } from "@/states/guardian";
import {
  estateContext,
  sameGuardianContext,
  type GuardianContext,
} from "./guardianContext";
import { useGuardian } from "./GuardianProvider";
import "./guardian-rail.scss";

const GuardianTranscript = React.lazy(() => import("./GuardianTranscript"));

/** A non-modal assistant: page navigation remains available in either mode. */
const GuardianRail: React.FC<{ open: boolean; isBlocked: boolean }> = ({
  open,
  isBlocked,
}) => {
  const { t } = useTranslation();
  const guardian = useGuardian();
  // Held in state, not a ref: `appendTo` is read during render, and a ref is
  // still null on the first one.
  const [railEl, setRailEl] = useState<HTMLElement | null>(null);
  const [selectorOpen, setSelectorOpen] = useState(false);
  const lastMode = useFrozenValue(guardian.displayMode, open);
  const docked = lastMode === "docked";
  const { startResize, resizeTo, isResizing } = useResizableWidth({
    resizable: docked && open && !isBlocked && guardian.canResizeDocked,
    defaultVw: guardian.minDockedWidthVw,
    widthVw: guardian.dockedWidthVw,
    onWidthChange: guardian.setDockedWidth,
    minVw: guardian.minDockedWidthVw,
    maxVw: GUARDIAN_MAX_DOCKED_VW,
  });
  const expanded = guardian.dockedWidthVw >= GUARDIAN_MAX_DOCKED_VW;
  const contexts = useMemo(() => {
    const estate = estateContext(t("trustAiBoard.guardian.kind.estate"));
    return [guardian.context, estate, ...guardian.recentContexts].filter(
      (item, index, all) =>
        all.findIndex(candidate => sameGuardianContext(candidate, item)) ===
        index,
    );
  }, [guardian.context, guardian.recentContexts, t]);

  useEffect(() => {
    if (isBlocked) setSelectorOpen(false);
  }, [isBlocked]);

  return (
    <aside
      ref={setRailEl}
      style={docked ? { width: guardian.dockedWidth } : undefined}
      className={`tlx-guardian-rail tlx-guardian-rail--${docked ? "docked" : "floating"} ${open ? "tlx-guardian-rail--open" : ""}`}
      role="complementary"
      aria-label={t("trustAiBoard.guardian.railLabel")}
      inert={!open || isBlocked || undefined}
      data-test-id="trustai-guardian-rail"
      data-display-mode={lastMode}
      data-resizing={isResizing}
      onKeyDown={event => {
        if (event.key !== "Escape" || isBlocked) return;
        event.stopPropagation();
        if (selectorOpen) setSelectorOpen(false);
        else guardian.close();
      }}
    >
      {docked && guardian.canResizeDocked && (
        <div
          className="tlx-panel-resize-handle"
          role="separator"
          tabIndex={0}
          aria-orientation="vertical"
          aria-label={t("trustAiBoard.guardian.resize")}
          aria-valuemin={Math.round(guardian.minDockedWidthVw)}
          aria-valuemax={GUARDIAN_MAX_DOCKED_VW}
          aria-valuenow={Math.round(guardian.dockedWidthVw)}
          aria-valuetext={t("trustAiBoard.guardian.widthPercent", {
            percent: Math.round(guardian.dockedWidthVw),
          })}
          onMouseDown={startResize}
          onKeyDown={event => {
            const widths: Record<string, number> = {
              ArrowLeft: guardian.dockedWidthVw + 2,
              ArrowRight: guardian.dockedWidthVw - 2,
              Home: guardian.minDockedWidthVw,
              End: GUARDIAN_MAX_DOCKED_VW,
            };
            if (widths[event.key] === undefined || isBlocked) return;
            event.preventDefault();
            event.stopPropagation();
            resizeTo(widths[event.key]);
          }}
          data-test-id="guardian-resize-handle"
        >
          <span className="tlx-panel-resize-handle__grip" aria-hidden="true" />
        </div>
      )}
      <header className="tlx-guardian-rail__header">
        <div className="tlx-guardian-rail__heading">
          <h2 className="tlx-guardian-rail__title">
            <Sparkles size={16} aria-hidden="true" />
            {t("trustAiBoard.guardian.railLabel")}
          </h2>
          <div
            className="tlx-guardian-rail__context"
            data-test-id="trustai-guardian-context"
          >
            <small>{t("trustAiBoard.guardian.contextLabel")}</small>
            <div data-test-id="guardian-context-selector">
              <DropDownList
                ariaLabel={t("trustAiBoard.guardian.switchContext")}
                data={contexts}
                textField="label"
                value={guardian.context}
                opened={selectorOpen}
                onOpen={() => setSelectorOpen(true)}
                onClose={() => setSelectorOpen(false)}
                popupSettings={{ appendTo: railEl ?? undefined }}
                onChange={event => {
                  setSelectorOpen(false);
                  guardian.setContext(event.value as GuardianContext);
                }}
              />
            </div>
          </div>
        </div>
        <div className="tlx-guardian-rail__header-actions">
          {docked && guardian.canResizeDocked && (
            <Button
              type="button"
              fillMode="flat"
              size="small"
              onClick={() =>
                resizeTo(
                  expanded ? guardian.minDockedWidthVw : GUARDIAN_MAX_DOCKED_VW,
                )
              }
              aria-label={t(
                `trustAiBoard.guardian.${expanded ? "restoreWidth" : "expand"}`,
              )}
              title={t(
                `trustAiBoard.guardian.${expanded ? "restoreWidth" : "expand"}`,
              )}
              data-test-id="guardian-expand-toggle"
            >
              {expanded ? (
                <Minimize2 size={18} aria-hidden="true" />
              ) : (
                <Maximize2 size={18} aria-hidden="true" />
              )}
            </Button>
          )}
          <Button
            type="button"
            fillMode="flat"
            size="small"
            onClick={docked ? guardian.undock : guardian.dock}
            aria-label={t(
              `trustAiBoard.guardian.${docked ? "undock" : "dock"}`,
            )}
            data-test-id="guardian-dock-toggle"
          >
            {docked ? (
              <PanelRightOpen size={18} aria-hidden="true" />
            ) : (
              <PanelRightClose size={18} aria-hidden="true" />
            )}
          </Button>
          <Button
            type="button"
            fillMode="flat"
            size="small"
            onClick={guardian.close}
            aria-label={t("trustAiBoard.guardian.close")}
            data-test-id="trustai-guardian-close"
          >
            <X size={18} aria-hidden="true" />
          </Button>
        </div>
      </header>
      <Suspense
        fallback={<p role="status">{t("trustAiBoard.guardian.loading")}</p>}
      >
        <GuardianTranscript isBlocked={isBlocked} />
      </Suspense>
    </aside>
  );
};
export default GuardianRail;
